import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, BadgePoundSterling, Star, BadgeCheck } from "lucide-react";
import { formatSalary, timeAgo, fileUrl, api } from "../lib/api";
import { sourceMeta } from "../lib/sources";
import JobStatusNotes from "./JobStatusNotes";

function CompanyMark({ job, featured }) {
  const [err, setErr] = useState(false);
  const masked = !!job.source && job.source !== "native" &&
    (job.company_name || "").toLowerCase().replace(/[^a-z]/g, "") ===
    sourceMeta(job.source).label.toLowerCase().replace(/[^a-z]/g, "");
  if (job.company_logo && !err && !masked) {
    return (
      <img src={fileUrl(job.company_logo)} alt={`${job.company_name} logo`} onError={() => setErr(true)}
        data-testid={job.slug ? `job-logo-${job.slug}` : `ext-logo-${job.id}`}
        className="shrink-0 h-10 w-10 sm:h-12 sm:w-12 rounded-md object-cover border border-zinc-200 bg-white" />
    );
  }
  return (
    <div className={`shrink-0 h-10 w-10 sm:h-12 sm:w-12 rounded-md flex items-center justify-center font-display font-bold text-base sm:text-lg ${
      featured ? "bg-yellow-100 text-yellow-800" : "bg-zinc-100 text-zinc-700"
    }`}>
      {masked ? "?" : (job.company_name?.charAt(0)?.toUpperCase() || "?")}
    </div>
  );
}

/** Whole card links to the on-site detail page (native and partner), jobseekers always
 *  see the full listing inside HiredToday first; Apply lives on the detail page. */
export default function JobCard({ job, index = 0, onSelect = null, selected = false }) {
  const salary = formatSalary(job);
  const featured = job.tier === "featured";
  const isExt = !!job.source && job.source !== "native";
  const href = isExt ? `/jobs/ext/${job.source}/${job.id}` : `/jobs/${job.slug}`;
  // Apply: every card opens the on-site detail page first — the tracked partner
  // redirect ("Apply on partner website") lives only on the detail page
  const navigate = useNavigate();
  const goApply = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(href);
  };
  const keyApply = (e) => { if (e.key === "Enter" || e.key === " ") goApply(e); };
  const extType = { full_time: "Full-time", part_time: "Part-time" }[job.contract_time] || "";
  // never present the provider as the employer — masked feeds show an honest label
  const maskedEmployer = isExt && (job.company_name || "").toLowerCase().replace(/[^a-z]/g, "")
    === sourceMeta(job.source).label.toLowerCase().replace(/[^a-z]/g, "");
  const employerShown = maskedEmployer ? "Employer not disclosed" : job.company_name;
  // anonymised learning: which search words led to this job being opened (word pairs only)
  const learn = () => {
    try {
      const q = new URLSearchParams(window.location.search).get("query");
      if (q && job.title) api.post("/jobs/learn-search", { query: q, title: job.title }).catch(() => {});
    } catch (_) { /* best-effort */ }
  };
  const cardClick = (e) => {
    learn();
    if (onSelect) { e.preventDefault(); onSelect(job); }
  };
  return (
    <Link
      to={href}
      onClick={cardClick}
      aria-current={selected ? "true" : undefined}
      data-testid={isExt ? `ext-job-${job.id}` : `job-card-${job.slug}`}
      aria-label={`${job.title} at ${job.company_name}, ${job.location}`}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      className={`animate-fade-up group relative block bg-white border rounded-md p-4 sm:p-6 transition-[box-shadow,border-color] duration-200 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${
        selected ? "border-zinc-400 ring-1 ring-zinc-300 shadow-md bg-zinc-50/60"
          : featured ? "border-yellow-300 bg-yellow-50/40 hover:border-yellow-400" : "border-zinc-200 hover:border-zinc-400"
      }`}
    >
      {featured && (
        <span data-testid={`featured-badge-${job.slug}`} className="absolute -top-2 left-4 z-10 text-[10px] font-bold uppercase tracking-wider bg-yellow-400 text-zinc-950 px-1.5 py-0.5 rounded shadow-sm">
          Featured
        </span>
      )}
      <div className="flex items-start gap-3 sm:gap-4">
        <CompanyMark job={job} featured={featured} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2 sm:gap-3">
            <h3 className="min-w-0 flex-1 font-display font-bold text-base sm:text-lg text-zinc-950 leading-snug group-hover:text-blue-700 transition-colors duration-200">
              {job.title}
            </h3>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {job.urgent && (
                <span data-testid={`urgent-badge-${job.slug || job.id}`} className="text-[11px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded">
                  Urgent
                </span>
              )}
              <span role="button" tabIndex={0} data-testid={`card-apply-${job.slug || job.id}`}
                onClick={goApply} onKeyDown={keyApply}
                className="inline-flex items-center justify-center min-h-[44px] sm:min-h-0 text-xs font-semibold bg-zinc-950 text-white px-3.5 py-2 sm:py-1.5 rounded-md hover:bg-zinc-800 transition-colors cursor-pointer">
                Apply
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <p className="text-sm font-medium text-zinc-600">{employerShown}</p>
            {!isExt && (
              <>
                <span title={job.employer_verified ? "Verified company" : "Not verified yet"}
                  data-testid={`verified-dot-${job.slug}`} className="inline-flex shrink-0">
                  <BadgeCheck className={`h-4 w-4 ${job.employer_verified ? "text-blue-600" : "text-zinc-300"}`} />
                </span>
                <span className="hidden sm:inline-flex items-center gap-0.5 ml-1" data-testid={`rating-${job.slug}`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`h-3 w-3 ${n <= Math.round(job.rating_avg || 0) ? "fill-amber-400 text-amber-400" : "fill-zinc-200 text-zinc-200"}`} />
                  ))}
                  <span className="text-xs text-zinc-500 ml-1">{(job.rating_avg || 0).toFixed(1)} ({job.rating_count || 0})</span>
                </span>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 mt-2 sm:mt-2.5 text-xs sm:text-sm text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{job.location}{job.distance_miles != null && <span className="text-zinc-400" data-testid="job-distance"> · {job.distance_miles < 1 ? "<1" : Math.round(job.distance_miles)} mi away</span>}</span>
            {(job.job_type || extType) && (
              <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{job.job_type || extType}</span>
            )}
            {salary && <span className="inline-flex items-center gap-1.5"><BadgePoundSterling className="h-3.5 w-3.5" />{salary}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 sm:mt-4 flex-wrap">
        {/* single evidence-based status badge — left cards only, never in the details pane */}
        <JobStatusNotes job={job} isExt={isExt} maskedEmployer={maskedEmployer} />
        {job.category && <span className="text-xs font-medium bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-full">{job.category}</span>}
        {job.workplace && <span className="text-xs font-medium bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-full">{job.workplace}</span>}
        <span className="ml-auto text-xs text-zinc-400">{timeAgo(job.live_at || job.created_at)}</span>
      </div>
    </Link>
  );
}
